import { useAppDispatch } from "../../../store";
import { useCallback, useContext, useState } from "react";
import {
  pageFailure,
  removeShipmentInStore,
  Shipment,
  updateShipment,
  addShipment as addShipmentAction,
} from "../../../../../reducers/uploadedBoard";
import { useSendUpdateEvent } from "./sendUpdates";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../state-machine/sendBoardEvent";
import { useRefreshBoardInStore } from "../boardRefreshes";
import useManufacturingDashboard from "../../../manfacturingDashboard";
import { ShipmentUpdate } from "../../../../../reducers/models/boardUpdates";
import useFetchAndTrace from "../../../fetchWithTrace";
import settings from "../../../../../constants/settings";

export function useUpdateShipments() {
  const fetchAndTrace = useFetchAndTrace();
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const [addingShipment, setAddingShipment] = useState(false);
  const [updatingShipment, setUpdatingShipment] = useState(false);
  const [purchasingShippingLabel, setPurchasingShippingLabel] = useState(false);
  const [updatingManufacturedStatus, setUpdatingManufacturedStatus] =
    useState(false);
  const { refreshBoard } = useRefreshBoardInStore();
  const { refreshDashboard } = useManufacturingDashboard();
  const { sendUpdateEvent } = useSendUpdateEvent();

  const updateShipmentDetails = useCallback(
    (shipmentId: string, update: ShipmentUpdate, onUpdate: () => void) => {
      if (boardId) {
        setUpdatingShipment(true);
        dispatch(updateShipment({ boardId, shipmentId, update }));
        void sendUpdateEvent(
          {
            machine_group: MachineGroup.SHIPMENT,
            model_id: shipmentId,
            event: BoardTransitionEvent.SHOULD_UPDATE_SHIPMENT,
            update: update,
          },
          () => {
            refreshBoard();
            setUpdatingShipment(false);
            onUpdate();
          },
        );
      }
    },
    [boardId, dispatch, sendUpdateEvent, refreshBoard],
  );

  const addShipment = useCallback(
    (update: ShipmentUpdate, onAdd: () => void) => {
      const handler = async (response: Response) => {
        setAddingShipment(false);
        onAdd();
        if (response.ok) {
          const shipment = (await response.json()) as Shipment;
          dispatch(
            addShipmentAction({
              boardId,
              shipment: {
                ...shipment,
                ...update,
              },
            }),
          );
        } else {
          throw Error("Failed to update shipment");
        }
      };

      const errorHandler = () => {
        if (boardId) {
          dispatch(pageFailure({ boardId }));
          setAddingShipment(false);
          onAdd();
        }
      };

      if (boardId) {
        setAddingShipment(true);
        void fetchAndTrace(
          `${settings.api_url}/boards/${boardId}/shipments`,
          handler,
          errorHandler,
          {
            body: JSON.stringify(update),
            method: "POST",
          },
        );
      }
    },
    [boardId, dispatch, fetchAndTrace],
  );

  const removeShipment = useCallback(
    (shipmentId: string) => {
      if (boardId) {
        setUpdatingShipment(true);
        void sendUpdateEvent(
          {
            machine_group: MachineGroup.SHIPMENT,
            model_id: shipmentId,
            event: BoardTransitionEvent.SHOULD_REMOVE_SHIPMENT,
            update: { shipment_id: shipmentId },
          },
          () => {
            dispatch(
              removeShipmentInStore({
                boardId,
                shipmentId,
              }),
            );
            setUpdatingShipment(false);
          },
        );
      }
    },
    [boardId, sendUpdateEvent, dispatch],
  );

  const purchaseShippingLabel = useCallback(
    (
      shipmentId: string,
      shippoRateObjectId: string,
      onPurchase: () => void,
    ) => {
      if (boardId) {
        setPurchasingShippingLabel(true);
        void sendUpdateEvent(
          {
            machine_group: MachineGroup.SHIPMENT,
            model_id: shipmentId,
            event: BoardTransitionEvent.SHIPMENT_SHOULD_PURCHASE_LABEL,
            update: {
              shipment_id: shipmentId,
              shippo_rate_object_id: shippoRateObjectId,
            },
          },
          () => {
            refreshBoard();
            setPurchasingShippingLabel(false);
            onPurchase();
          },
        );
      }
    },
    [boardId, sendUpdateEvent, refreshBoard],
  );

  const updateManufacturedStatus = useCallback(
    (shipmentId: string, manufactured: boolean, notifyCustomer: boolean) => {
      if (boardId) {
        setUpdatingManufacturedStatus(true);
        void sendUpdateEvent(
          {
            machine_group: MachineGroup.SHIPMENT,
            model_id: shipmentId,
            event: BoardTransitionEvent.SHIPMENT_SHOULD_UPDATE_MANUFACTURED,
            update: {
              shipment_id: shipmentId,
              manufactured: manufactured,
              notify_customer: notifyCustomer,
            },
          },
          () => {
            refreshBoard();
            refreshDashboard();
            setUpdatingManufacturedStatus(false);
          },
        );
      }
    },
    [boardId, sendUpdateEvent, refreshBoard, refreshDashboard],
  );

  return {
    updatingShipment,
    addShipment,
    addingShipment,
    removeShipment,
    purchaseShippingLabel,
    purchasingShippingLabel,
    updateManufacturedStatus,
    updatingManufacturedStatus,
    updateShipmentDetails,
  };
}
